import React, { useCallback, useEffect, useState, useRef, lazy } from 'react';
import { Hero } from '../components/Hero';
import { Section } from '../components/Section';
import { PullQuote } from '../components/PullQuote';
import { PurchaseSection } from '../components/PurchaseSection';
import { CTA } from '../components/CTA';
import { SpiritualLoader } from '../components/SpiritualLoader';
import { StepCard } from '../components/StepCard';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
export function SalesPage() {
  const {
    t
  } = useLanguage();
  const purchaseRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  // Throttle mouse movement for better performance
  const throttleTimeout = useRef<NodeJS.Timeout>();
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (throttleTimeout.current) return;
      throttleTimeout.current = setTimeout(() => {
        setMousePosition({
          x: e.clientX,
          y: e.clientY
        });
        throttleTimeout.current = undefined;
      }, 50);
    };
    window.addEventListener('mousemove', handleMouseMove, {
      passive: true
    });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (throttleTimeout.current) clearTimeout(throttleTimeout.current);
    };
  }, []);
  const scrollToPurchase = useCallback(() => {
    purchaseRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }, []);
  return <>
      <SpiritualLoader data-id="element-107" />
      <PurchaseNotification data-id="element-108" />

      {/* Simplified animated background - fewer trails for better performance */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{
      zIndex: 0
    }} data-id="element-109">
        {/* Optimized mouse spotlight effect */}
        <div className="mouse-spotlight hidden md:block" style={{
        left: `${mousePosition.x}px`,
        top: `${mousePosition.y}px`,
        willChange: 'transform'
      }} data-id="element-110" />

        <div className="energy-trails-bg" data-id="element-111">
          <div className="light-trail trail-1" data-id="element-112"></div>
          <div className="light-trail trail-2" data-id="element-113"></div>
          <div className="light-trail trail-3" data-id="element-114"></div>
          <div className="light-trail trail-4" data-id="element-115"></div>
        </div>
      </div>

      <div className="min-h-screen relative" data-id="element-116">
        <Hero onScrollToPurchase={scrollToPurchase} data-id="element-117" />

        <main className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-10 space-y-10 md:space-y-16" data-id="element-118">
          <Section data-id="element-119">
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A]" data-id="element-120">
              {t.intro.p2}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mt-4 md:mt-6" data-id="element-121">
              {t.intro.p3}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mt-4 md:mt-6 italic" data-id="element-122">
              {t.intro.p4}
            </p>
          </Section>

          <Section variant="gold" data-id="element-123">
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-5 md:mb-8" data-id="element-124">
              {t.pain.title}
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A]" data-id="element-125">
              {t.pain.p1}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mt-4 md:mt-6" data-id="element-126">
              {t.pain.p2}
            </p>
          </Section>

          <Section data-id="element-127">
            <h3 className="font-serif text-xl md:text-3xl text-[#2C4A3E] mb-4 md:mb-6" data-id="element-128">
              {t.blocks.sabotage.title}
            </h3>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A]" data-id="element-129">
              {t.blocks.sabotage.text}
            </p>
          </Section>

          <Section variant="green" data-id="element-130">
            <h3 className="font-serif text-xl md:text-3xl text-[#2C4A3E] mb-4 md:mb-6" data-id="element-131">
              {t.blocks.guilt.title}
            </h3>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-6 md:mb-8" data-id="element-132">
              {t.blocks.guilt.text}
            </p>

            <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true,
            amount: 0.3
          }} transition={{
            duration: 0.5
          }} className="my-6 md:my-8" data-id="element-133">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/brTjScpi9Z6qbUNZTwLFxd/ChatGPT_Image_30_nov_2025%2C_18_11_48.png" alt="Tres generaciones de mujeres trabajando" className="w-full rounded-2xl shadow-2xl" loading="lazy" decoding="async" data-id="element-134" />
            </motion.div>
          </Section>

          <Section data-id="element-135">
            <h3 className="font-serif text-xl md:text-3xl text-[#2C4A3E] mb-4 md:mb-6" data-id="element-136">
              {t.blocks.fear.title}
            </h3>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A]" data-id="element-137">
              {t.blocks.fear.text}
            </p>
          </Section>

          <Section variant="gold" data-id="element-138">
            <h3 className="font-serif text-xl md:text-3xl text-[#2C4A3E] mb-4 md:mb-6" data-id="element-139">
              {t.blocks.energetic.title}
            </h3>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A]" data-id="element-140">
              {t.blocks.energetic.text}
            </p>
          </Section>

          <Section data-id="element-141">
            <h3 className="font-serif text-xl md:text-3xl text-[#2C4A3E] mb-4 md:mb-6" data-id="element-142">
              {t.blocks.anxiety.title}
            </h3>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A]" data-id="element-143">
              {t.blocks.anxiety.text}
            </p>
          </Section>

          <PullQuote data-id="element-144">{t.pullQuote}</PullQuote>

          <Section variant="green" data-id="element-145">
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-5 md:mb-8" data-id="element-146">
              {t.vision.title}
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-147">
              {t.vision.intro1}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-6 md:mb-8" data-id="element-148">
              {t.vision.intro2}
            </p>

            <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true,
            amount: 0.3
          }} transition={{
            duration: 0.5
          }} className="my-6 md:my-10" data-id="element-149">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fK8V5w1B5z2x7S7bmgRaCR/ChatGPT_Image_3_dic_2025%2C_12_26_55.png" alt="Mujer en paz sosteniendo luz interior" className="w-full rounded-2xl shadow-2xl" loading="lazy" decoding="async" data-id="element-150" />
            </motion.div>

            <ul className="space-y-3 md:space-y-4 text-base md:text-lg text-[#4A4A4A]" data-id="element-151">
              {t.vision.items.map((item, index) => <li key={index} className="flex items-start" data-id="element-152">
                  <span className="text-[#D4A574] mr-3" data-id="element-153">•</span>
                  <span data-id="element-154">{item}</span>
                </li>)}
            </ul>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mt-6 md:mt-8 italic" data-id="element-155">
              {t.vision.closing}
            </p>
          </Section>

          <Section data-id="element-156">
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-5 md:mb-8" data-id="element-157">
              {t.truth.title}
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-158">
              {t.truth.intro}
            </p>
            <ul className="space-y-2 md:space-y-3 text-base md:text-lg text-[#4A4A4A] mb-4 md:mb-6" data-id="element-159">
              {t.truth.methods.map((method, index) => <li key={index} className="flex items-start" data-id="element-160">
                  <span className="text-[#D4A574] mr-3" data-id="element-161">•</span>
                  <span data-id="element-162">{method}</span>
                </li>)}
            </ul>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A]" data-id="element-163">
              {t.truth.p1}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mt-4 md:mt-6 font-semibold" data-id="element-164">
              {t.truth.p2}
            </p>
          </Section>

          <Section variant="gold" data-id="element-165">
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-5 md:mb-8" data-id="element-166">
              {t.origin.title}
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-167">
              {t.origin.p1}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-168">
              {t.origin.p2}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6 italic" data-id="element-169">
              {t.origin.p3}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A]" data-id="element-170">
              {t.origin.p4}
            </p>
          </Section>

          <Section data-id="element-171">
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-5 md:mb-8" data-id="element-172">
              {t.solution.title}
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-173">
              {t.solution.intro1}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-6 md:mb-8" data-id="element-174">
              {t.solution.intro2}
            </p>

            <div className="space-y-4 md:space-y-6" data-id="element-175">
              {t.solution.steps.map((step, index) => <StepCard key={index} number={index + 1} text={step} delay={0} data-id="element-176" />)}
            </div>

            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mt-6 md:mt-8 font-semibold" data-id="element-177">
              {t.solution.closing}
            </p>
          </Section>

          <Section variant="green" data-id="element-178">
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-5 md:mb-8" data-id="element-179">
              {t.story.title}
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-180">
              {t.story.p1}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-181">
              {t.story.p2}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-182">
              {t.story.p3}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-183">
              {t.story.p4}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-184">
              {t.story.p5}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-6 md:mb-8 italic" data-id="element-185">
              {t.story.p6}
            </p>

            <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true,
            amount: 0.3
          }} transition={{
            duration: 0.5
          }} className="mt-6 md:mt-8" data-id="element-186">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/iFQ1a1HWhWeNkip9G76wAL/ChatGPT_Image_28_nov_2025%2C_11_11_27B.png" alt="Mujer sonriendo mientras lee el libro" className="w-full rounded-2xl shadow-2xl" loading="lazy" decoding="async" data-id="element-187" />
            </motion.div>
          </Section>

          <Section data-id="element-188">
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-5 md:mb-8" data-id="element-189">
              {t.benefits.title}
            </h2>
            <ul className="space-y-3 md:space-y-4 text-base md:text-lg text-[#4A4A4A]" data-id="element-190">
              {t.benefits.items.map((item, index) => <li key={index} className="flex items-start" data-id="element-191">
                  <span className="text-[#D4A574] mr-3" data-id="element-192">•</span>
                  <span data-id="element-193">{item}</span>
                </li>)}
            </ul>
          </Section>

          <Section variant="gold" data-id="element-194">
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-5 md:mb-8" data-id="element-195">
              {t.invitation.title}
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-196">
              {t.invitation.p1}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-197">
              {t.invitation.p2}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6 font-semibold" data-id="element-198">
              {t.invitation.p3}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-199">
              {t.invitation.p4}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] italic" data-id="element-200">
              {t.invitation.p5}
            </p>
          </Section>

          <div ref={purchaseRef} data-id="element-201">
            <PurchaseSection data-id="element-202" />
          </div>

          <CTA data-id="element-203" />
        </main>
      </div>

      <style data-id="element-204">{`
        /* Optimized mouse spotlight - reduced complexity */
        .mouse-spotlight {
          position: fixed;
          width: 600px;
          height: 600px;
          border-radius: 50%;
          background: radial-gradient(
            circle,
            rgba(232, 184, 109, 0.25) 0%,
            rgba(212, 165, 116, 0.15) 20%,
            rgba(245, 230, 211, 0.08) 40%,
            transparent 70%
          );
          transform: translate(-50%, -50%);
          pointer-events: none;
          mix-blend-mode: screen;
          filter: blur(40px);
        }

        .energy-trails-bg {
          position: absolute;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }

        .light-trail {
          position: absolute;
          width: 300px;
          height: 2px;
          opacity: 0;
          will-change: transform, opacity;
        }

        @media (max-width: 768px) {
          .light-trail {
            width: 200px;
          }
        }

        /* Simplified trails - only 4 for better performance */
        .trail-1 {
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(232, 184, 109, 0.3) 30%, 
            rgba(212, 165, 116, 0.4) 50%, 
            rgba(232, 184, 109, 0.3) 70%, 
            transparent 100%);
          bottom: -200px;
          left: -200px;
          transform: rotate(45deg);
          animation: trail-flow-1 10s ease-in-out infinite;
        }

        .trail-2 {
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(245, 230, 211, 0.25) 30%, 
            rgba(212, 165, 116, 0.35) 50%, 
            rgba(245, 230, 211, 0.25) 70%, 
            transparent 100%);
          top: -100px;
          right: -200px;
          transform: rotate(-45deg);
          animation: trail-flow-2 12s ease-in-out infinite;
          animation-delay: 3s;
        }

        .trail-3 {
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(232, 184, 109, 0.2) 30%, 
            rgba(255, 255, 255, 0.25) 50%, 
            rgba(232, 184, 109, 0.2) 70%, 
            transparent 100%);
          top: 40%;
          left: -300px;
          animation: trail-flow-3 14s ease-in-out infinite;
          animation-delay: 5s;
        }

        .trail-4 {
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(212, 165, 116, 0.3) 30%, 
            rgba(232, 184, 109, 0.4) 50%, 
            rgba(212, 165, 116, 0.3) 70%, 
            transparent 100%);
          bottom: 30%;
          right: -300px;
          transform: rotate(30deg);
          animation: trail-flow-4 11s ease-in-out infinite;
          animation-delay: 2s;
        }

        /* Optimized keyframes */
        @keyframes trail-flow-1 {
          0%, 100% {
            opacity: 0;
            transform: translate(0, 0) rotate(45deg);
          }
          50% {
            opacity: 1;
            transform: translate(1200px, -1200px) rotate(45deg);
          }
        }

        @keyframes trail-flow-2 {
          0%, 100% {
            opacity: 0;
            transform: translate(0, 0) rotate(-45deg);
          }
          50% {
            opacity: 1;
            transform: translate(-1200px, 1200px) rotate(-45deg);
          }
        }

        @keyframes trail-flow-3 {
          0%, 100% {
            opacity: 0;
            transform: translateX(0);
          }
          50% {
            opacity: 1;
            transform: translateX(calc(100vw + 300px));
          }
        }

        @keyframes trail-flow-4 {
          0%, 100% {
            opacity: 0;
            transform: translate(0, 0) rotate(30deg);
          }
          50% {
            opacity: 1;
            transform: translate(-1000px, -600px) rotate(30deg);
          }
        }
      `}</style>
    </>;
}